/**
 * Closing and reopening a ticket.
 *
 * Both are `AlertDialog`s rather than plain dialogs: an accidental click on the
 * backdrop must not throw away what the employee typed, and closing a ticket is
 * the one action here they cannot undo themselves. Esc still closes, as it
 * always must.
 *
 * The close payload follows the contract exactly — Ivanti requires a resolution
 * and a cause code, so the submit button stays disabled until both are present
 * rather than letting the user discover the 422.
 */

import { Alert, AlertDialog, Button, Field, Textarea, Typography } from "@norican/react";
import { useEffect, useState } from "react";

import { ErrorState, LoadingState } from "@/components/common";
import { useLanguage, useT } from "@/i18n";

import { useCloseTicket, useReopenTicket, useTicketMetadata } from "../api/queries";
import { useFeedbackToast } from "../lib/use-feedback-toast";
import { MAX_BODY_LENGTH } from "./compose-form";
import { LookupSelect } from "./lookup-select";

export interface TicketCloseDialogProps {
  ticketId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** IT was asked to close it rather than it being closed (untriaged incident). */
  onClosureRequested?: () => void;
}

export function TicketCloseDialog({
  ticketId,
  open,
  onOpenChange,
  onClosureRequested,
}: TicketCloseDialogProps) {
  const { t } = useT("tickets");
  const { t: tCommon } = useT();
  const { language } = useLanguage();
  const toast = useFeedbackToast();
  const metadata = useTicketMetadata();
  const mutation = useCloseTicket(ticketId);

  const [resolution, setResolution] = useState("");
  const [causeCodeId, setCauseCodeId] = useState<string | null>(null);

  // A failure from the last attempt must not greet the next opening.
  const { reset } = mutation;
  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const causeCodes = metadata.data?.cause_codes ?? [];
  const canSubmit = resolution.trim().length > 0 && causeCodeId !== null && !mutation.isPending;

  const handleSubmit = async () => {
    if (causeCodeId === null) {
      return;
    }
    try {
      const detail = await mutation.mutateAsync({
        resolution: resolution.trim(),
        resolution_lang: language,
        cause_code_id: causeCodeId,
        // A technician's KPI, not the requester's to answer.
        first_call_resolution: false,
      });
      onOpenChange(false);
      setResolution("");
      // An untriaged incident cannot be closed from here; IT was asked to.
      toast.success(t(detail.closure_requested ? "close.requested" : "close.success"));
      if (detail.closure_requested) {
        onClosureRequested?.();
      }
    } catch {
      // Shown inline above the form; a toast as well would say it twice.
    }
  };

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Backdrop />
        <AlertDialog.Popup className="flex w-full max-w-lg flex-col gap-4">
          <AlertDialog.Title>{t("close.title")}</AlertDialog.Title>
          <AlertDialog.Description>{t("close.description")}</AlertDialog.Description>

          {mutation.error === null ? null : <ErrorState variant="inline" error={mutation.error} />}

          {metadata.isPending ? (
            <LoadingState inline label={tCommon("state.loading")} />
          ) : metadata.isError ? (
            <ErrorState
              variant="inline"
              error={metadata.error}
              onRetry={() => void metadata.refetch()}
            />
          ) : (
            <div className="flex flex-col gap-4">
              <Field
                name="resolution"
                label={t("close.resolutionLabel")}
                hint={t("close.resolutionHint")}
                required
              >
                <Textarea
                  name="resolution"
                  value={resolution}
                  onChange={(event) => setResolution(event.target.value)}
                  placeholder={t("close.resolutionPlaceholder")}
                  rows={4}
                  autoResize
                  maxLength={MAX_BODY_LENGTH}
                />
              </Field>

              <Field name="cause_code" label={t("close.causeLabel")} required>
                <LookupSelect
                  name="cause_code"
                  items={causeCodes}
                  value={causeCodeId}
                  onValueChange={setCauseCodeId}
                  placeholder={t("close.causePlaceholder")}
                  aria-label={t("close.causeLabel")}
                />
              </Field>

              {causeCodes.length === 0 ? (
                <Alert tone="warning">{t("close.noCauseCodes")}</Alert>
              ) : null}

              <Typography variant="caption" tone="muted">
                {t("close.irreversible")}
              </Typography>
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2">
            <AlertDialog.Close
              render={<Button variant="ghost" tone="neutral" />}
              disabled={mutation.isPending}
            >
              {tCommon("button.cancel")}
            </AlertDialog.Close>
            <Button
              tone="danger"
              loading={mutation.isPending}
              disabled={!canSubmit}
              onClick={() => void handleSubmit()}
            >
              {t("close.confirm")}
            </Button>
          </div>
        </AlertDialog.Popup>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

export interface TicketReopenDialogProps {
  ticketId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TicketReopenDialog({ ticketId, open, onOpenChange }: TicketReopenDialogProps) {
  const { t } = useT("tickets");
  const { t: tCommon } = useT();
  const { language } = useLanguage();
  const toast = useFeedbackToast();
  const mutation = useReopenTicket(ticketId);
  const [reason, setReason] = useState("");

  const { reset } = mutation;
  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const handleSubmit = async () => {
    try {
      await mutation.mutateAsync({ reason: reason.trim(), reason_lang: language });
      onOpenChange(false);
      setReason("");
      toast.success(t("reopen.success"));
    } catch {
      // Shown inline above the form; a toast as well would say it twice.
    }
  };

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Backdrop />
        <AlertDialog.Popup className="flex w-full max-w-lg flex-col gap-4">
          <AlertDialog.Title>{t("reopen.title")}</AlertDialog.Title>
          <AlertDialog.Description>{t("reopen.description")}</AlertDialog.Description>

          {mutation.error === null ? null : <ErrorState variant="inline" error={mutation.error} />}

          <Field name="reopen_reason" label={t("reopen.reasonLabel")} required>
            <Textarea
              name="reopen_reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t("reopen.reasonPlaceholder")}
              rows={3}
              autoResize
              maxLength={MAX_BODY_LENGTH}
            />
          </Field>

          <div className="flex flex-wrap justify-end gap-2">
            <AlertDialog.Close
              render={<Button variant="ghost" tone="neutral" />}
              disabled={mutation.isPending}
            >
              {tCommon("button.cancel")}
            </AlertDialog.Close>
            <Button
              loading={mutation.isPending}
              disabled={reason.trim().length === 0 || mutation.isPending}
              onClick={() => void handleSubmit()}
            >
              {t("reopen.confirm")}
            </Button>
          </div>
        </AlertDialog.Popup>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
